import React, { useState, useEffect } from 'react';
import { FiClock, FiCheckCircle, FiXCircle, FiEye, FiFilter, FiSearch, FiEdit2 } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { socket } from '../../socket/socket';

const BillUpdateApprovals = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'pending',
    search: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalComments, setApprovalComments] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [filters, currentPage]);

  // Listen for new bill update requests
  useEffect(() => {
    if (socket) {
      socket.on('billUpdateRequest', (data) => {
        toast.success(`New bill update request from ${data.vendorName} for order ${data.orderNumber}`, {
          duration: 5000,
          icon: '📝'
        });
        fetchRequests(); // Refresh the list
      });

      socket.on('billUpdateRequestCancelled', (data) => {
        toast.info(`Bill update request cancelled for order ${data.orderNumber}`, {
          duration: 4000,
          icon: '🚫'
        });
        fetchRequests(); // Refresh the list
      });

      return () => {
        socket.off('billUpdateRequest');
        socket.off('billUpdateRequestCancelled');
      };
    }
  }, [socket]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
      params.append('page', currentPage);
      params.append('limit', 10);

      const response = await api.get(`/vendor-owner/bill-update-requests?${params}`);
      setRequests(response.data.data);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching bill update requests:', error);
      toast.error('Failed to fetch bill update requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    setProcessing(true);
    try {
      await api.patch(`/vendor-owner/bill-update-requests/${requestId}/approve`, {
        comments: approvalComments
      });
      toast.success('Bill update request approved successfully!');
      setShowApprovalModal(false);
      setApprovalComments('');
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(error.response?.data?.message || 'Failed to approve request');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (requestId) => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setProcessing(true);
    try {
      await api.patch(`/vendor-owner/bill-update-requests/${requestId}/reject`, {
        rejectionReason
      });
      toast.success('Bill update request rejected successfully!');
      setShowRejectionModal(false);
      setRejectionReason('');
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error(error.response?.data?.message || 'Failed to reject request');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <FiClock className="w-4 h-4" />;
      case 'approved':
        return <FiCheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <FiXCircle className="w-4 h-4" />;
      case 'cancelled':
        return <FiXCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const openDetailsModal = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const openApprovalModal = (request) => {
    setSelectedRequest(request);
    setShowApprovalModal(true);
  };

  const openRejectionModal = (request) => {
    setSelectedRequest(request);
    setShowRejectionModal(true);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(price || 0);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Bill Update Approvals</h1>
        <p className="text-gray-600">Review and approve bill update requests from your vendors</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by order number or vendor..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              />
            </div>
          </div>
          
          <div className="min-w-[150px]">
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading requests...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">No bill update requests found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vendor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Update Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount Change
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reason
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {requests.map((request) => {
                  const originalTotal = request.originalBillDetails?.pricing?.total || 0;
                  const updatedTotal = request.updatedBillDetails?.pricing?.total || 0;
                  const amountChange = updatedTotal - originalTotal;
                  
                  return (
                    <tr key={request._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {request.vendorId?.personalDetails?.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {request.vendorId?.restaurantDetails?.restaurantName}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {request.tableId ? `Table ${request.tableId}` : request.orderId?.orderNumber || 'Unknown Order'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {request.tableId ? 
                              `Table Bill Update (${request.originalBillDetails?.items?.length || 0} items)` : 
                              `Bill: ${request.orderId?.billNumber || 'N/A'}`
                            }
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900 capitalize">
                          {request.updateType?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className="text-gray-900">
                            {formatPrice(originalTotal)} → {formatPrice(updatedTotal)}
                          </div>
                          <div className={`font-medium ${amountChange > 0 ? 'text-red-600' : amountChange < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                            {amountChange > 0 ? '+' : ''}{formatPrice(amountChange)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate" title={request.updateReason}>
                          {request.updateReason}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(request.status)}`}>
                          {getStatusIcon(request.status)}
                          <span className="ml-1 capitalize">{request.status}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(request.createdAt).toLocaleDateString()}
                        <br />
                        {new Date(request.createdAt).toLocaleTimeString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openDetailsModal(request)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <FiEye className="w-4 h-4" />
                          </button>
                          {request.status === 'pending' && (
                            <>
                              <button
                                onClick={() => openApprovalModal(request)}
                                className="text-green-600 hover:text-green-900"
                                title="Approve"
                              >
                                <FiCheckCircle className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openRejectionModal(request)}
                                className="text-red-600 hover:text-red-900"
                                title="Reject"
                              >
                                <FiXCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6 space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-4 py-2">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
              <h2 className="text-xl font-bold text-gray-800">
                Bill Update Request Details
              </h2>
              <button 
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none transition-colors" 
                onClick={() => setShowDetailsModal(false)}
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-2 gap-6">
                {/* Request Info */}
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-800 mb-3">Request Information</h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium">Vendor:</span>
                        <p className="text-gray-600">{selectedRequest.vendorId?.personalDetails?.name}</p>
                        <p className="text-gray-500">{selectedRequest.vendorId?.restaurantDetails?.restaurantName}</p>
                      </div>
                      <div>
                        <span className="font-medium">Order:</span>
                        <p className="text-gray-600">{selectedRequest.tableId ? `Table ${selectedRequest.tableId}` : selectedRequest.orderId?.orderNumber || 'Unknown Order'}</p>
                        <p className="text-gray-500">{selectedRequest.tableId ? 
                          `Table Bill Update (${selectedRequest.originalBillDetails?.items?.length || 0} items)` : 
                          `Bill: ${selectedRequest.orderId?.billNumber || 'N/A'}`
                        }</p>
                      </div>
                      <div>
                        <span className="font-medium">Update Type:</span>
                        <p className="text-gray-600 capitalize">{selectedRequest.updateType?.replace('_', ' ')}</p>
                      </div>
                      <div>
                        <span className="font-medium">Status:</span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(selectedRequest.status)}`}>
                          {getStatusIcon(selectedRequest.status)}
                          <span className="ml-1 capitalize">{selectedRequest.status}</span>
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Reason:</span>
                        <p className="text-gray-600">{selectedRequest.updateReason}</p>
                      </div>
                      {selectedRequest.rejectionReason && (
                        <div>
                          <span className="font-medium">Rejection Reason:</span>
                          <p className="text-red-600">{selectedRequest.rejectionReason}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Status History */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-800 mb-3">Status History</h3>
                    <div className="space-y-2">
                      {selectedRequest.statusHistory?.map((history, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-white rounded">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(history.status)}
                            <div>
                              <p className="font-medium text-sm capitalize">{history.status}</p>
                              <p className="text-xs text-gray-600">{history.comments}</p>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">
                            {new Date(history.timestamp).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Bill Comparison */}
                <div className="space-y-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-800 mb-3">Original Bill</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Subtotal:</span>
                        <span>{formatPrice(selectedRequest.originalBillDetails?.pricing?.subtotal)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>GST Amount:</span>
                        <span>{formatPrice(selectedRequest.originalBillDetails?.pricing?.gstAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Discount:</span>
                        <span>{formatPrice(selectedRequest.originalBillDetails?.pricing?.discount)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg border-t pt-2">
                        <span>Total:</span>
                        <span>{formatPrice(selectedRequest.originalBillDetails?.pricing?.total)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="font-semibold text-green-800 mb-3">Updated Bill</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Subtotal:</span>
                        <span>{formatPrice(selectedRequest.updatedBillDetails?.pricing?.subtotal)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>GST Amount:</span>
                        <span>{formatPrice(selectedRequest.updatedBillDetails?.pricing?.gstAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Discount:</span>
                        <span>{formatPrice(selectedRequest.updatedBillDetails?.pricing?.discount)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg border-t pt-2">
                        <span>Total:</span>
                        <span>{formatPrice(selectedRequest.updatedBillDetails?.pricing?.total)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Items Comparison */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-800 mb-3">Items Changes</h3>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedRequest.updatedBillDetails?.items?.map((item, index) => (
                        <div key={index} className="flex justify-between text-sm p-2 bg-white rounded">
                          <span>{item.name} x {item.quantity}</span>
                          <span>{formatPrice(item.subtotal)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Approve Bill Update</h3>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Are you sure you want to approve this bill update request?
                </p>
                <div className="bg-gray-50 rounded p-3 text-sm">
                  <p><strong>Order:</strong> {selectedRequest.orderId?.orderNumber}</p>
                  <p><strong>Vendor:</strong> {selectedRequest.vendorId?.personalDetails?.name}</p>
                  <p><strong>Amount Change:</strong> {formatPrice((selectedRequest.updatedBillDetails?.pricing?.total || 0) - (selectedRequest.originalBillDetails?.pricing?.total || 0))}</p>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comments (Optional)
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  rows="3"
                  value={approvalComments}
                  onChange={(e) => setApprovalComments(e.target.value)}
                  placeholder="Add any comments about this approval..."
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowApprovalModal(false);
                    setApprovalComments('');
                    setSelectedRequest(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleApprove(selectedRequest._id)}
                  disabled={processing}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {processing ? 'Processing...' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Reject Bill Update</h3>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Please provide a reason for rejecting this bill update request.
                </p>
                <div className="bg-gray-50 rounded p-3 text-sm">
                  <p><strong>Order:</strong> {selectedRequest.orderId?.orderNumber}</p>
                  <p><strong>Vendor:</strong> {selectedRequest.vendorId?.personalDetails?.name}</p>
                  <p><strong>Reason:</strong> {selectedRequest.updateReason}</p>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  rows="3"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Explain why this request is being rejected..."
                  required
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRejectionModal(false);
                    setRejectionReason('');
                    setSelectedRequest(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleReject(selectedRequest._id)}
                  disabled={processing}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {processing ? 'Processing...' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillUpdateApprovals;
